import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import React from "react";
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ReCAPTCHA from "react-google-recaptcha";
import { environment } from "../../../../utils/environment";
import {
  IAssignment,
  InviteUserModalController,
  InviteUserModalControllerDelegate,
} from "./controller";

interface State {
  isLoading: boolean;
  isSubmitting: boolean;
  assignmentsLoading: boolean;
  assignmentsInput: string;
  assignments: IAssignment[];
  selectedAssignments: IAssignment[];
  firstName: string;
  lastName: string;
  email: string;
  recaptchaToken: string;
}

interface Props {
  delegate: InviteUserModalDelegate;
}

export interface InviteUserModalDelegate {
  shouldCloseModal: () => void;
  applicationShouldLogout: () => void;
  genericError: () => void;
  didInviteUser: () => void;
}

class InviteUserModal
  extends React.Component<Props, State>
  implements InviteUserModalControllerDelegate {
  controller: InviteUserModalController;
  recaptchaRef = React.createRef<ReCAPTCHA>();
  private assignmentsFetchDebounce?: NodeJS.Timeout;

  constructor(props: Props) {
    super(props);
    this.state = {
      isLoading: true,
      isSubmitting: false,
      assignmentsLoading: false,
      assignmentsInput: "",
      assignments: [],
      selectedAssignments: [],
      firstName: "",
      lastName: "",
      email: "",
      recaptchaToken: "",
    };
    this.controller = new InviteUserModalController(this);
  }

  // delegate
  didGetAssignments(assignments: IAssignment[]) {
    this.setState({
      assignments,
    });
  }

  didInviteUser() {
    this.setState({ isSubmitting: false });
    this.props.delegate.didInviteUser();
  }

  genericError() {
    this.setState({ isSubmitting: false });
    this.props.delegate.genericError();
  }

  applicationShouldLogout() {
    this.setState({ isSubmitting: false });
    this.props.delegate.applicationShouldLogout();
  }

  handleChangeFirstName(event: any) {
    this.setState({
      firstName: event.target.value,
    });
  }

  handleChangeLastName(event: any) {
    this.setState({
      lastName: event.target.value,
    });
  }

  handleChangeEmail(value: string) {
    this.setState({
      email: value.toLowerCase().trim(),
    });
  }

  handleChangeSelected(selectedAssignments: IAssignment[]) {
    this.setState({
      selectedAssignments,
    });
  }

  componentDidMount() {
    this.fetchAssignments("");
  }

  didClickClose() {
    this.props.delegate.shouldCloseModal();
  }

  async onRecaptchaChange() {
    const recaptchaToken = this.recaptchaRef.current?.getValue() || "";
    this.setState({
      recaptchaToken,
    });
  }

  async didClickInviteUsers() {
    this.setState({
      isSubmitting: true,
    });
    const token = localStorage.getItem("AccessToken")!;
    const idsToAdd = this.state.selectedAssignments.map(
      (assignment) => assignment.applicationId
    );
    const { firstName, lastName, email, recaptchaToken } = this.state;
    this.controller.inviteUser(
      token,
      firstName,
      lastName,
      email,
      idsToAdd,
      recaptchaToken || ""
    );
  }

  private fetchAssignments(filter: string) {
    this.setState({ assignmentsLoading: true });
    clearTimeout(this.assignmentsFetchDebounce!);
    this.assignmentsFetchDebounce = setTimeout(async () => {
      try {
        const token = localStorage.getItem("AccessToken")!;
        const normalizedFilter = filter.trim();
        const params = {
          page: 1,
          limit: 15,
          ...(normalizedFilter ? { filter: normalizedFilter } : {}),
        };
        await this.controller.getManagedAssignments(token, params);
      } finally {
        this.setState({ assignmentsLoading: false, isLoading: false });
      }
    }, 300);
  }

  renderLoading() {
    return (
      <Box
        alignItems="center"
        display="flex"
        flexDirection="column"
        marginTop={2}
      >
        <CircularProgress />
      </Box>
    );
  }

  renderForm() {
    const assignmentsById = new Map<number, IAssignment>();
    this.state.selectedAssignments.forEach((assignment) => {
      if (assignment?.applicationId !== undefined) {
        assignmentsById.set(assignment.applicationId, assignment);
      }
    });
    this.state.assignments.forEach((assignment) => {
      if (assignment?.applicationId !== undefined) {
        assignmentsById.set(assignment.applicationId, assignment);
      }
    });
    const assignmentOptions = Array.from(assignmentsById.values());

    return (
      <Box display="flex" flexDirection="column" sx={{ gap: 2 }}>
        <Typography color="text.secondary">
          Digite os dados do usuário. Se ele não tiver uma conta, um convite
          será enviado.
        </Typography>
        <TextField
          variant="outlined"
          required
          label="Nome"
          fullWidth
          onChange={(event) => this.handleChangeFirstName(event)}
        />
        <TextField
          variant="outlined"
          required
          label="Sobrenome"
          fullWidth
          onChange={(event) => this.handleChangeLastName(event)}
        />
        <TextField
          variant="outlined"
          required
          label="E-mail"
          fullWidth
          onChange={(event) => this.handleChangeEmail(event.target.value)}
        />
        <Autocomplete
          multiple
          options={assignmentOptions}
          getOptionLabel={(assignment: IAssignment) =>
            `${assignment.title} (${assignment.instrumentTitle})`
          }
          value={this.state.selectedAssignments}
          onChange={(_, newValue) => this.handleChangeSelected(newValue)}
          inputValue={this.state.assignmentsInput}
          onInputChange={(_, value) => {
            this.setState({ assignmentsInput: value }, () => {
              this.fetchAssignments(value);
            });
          }}
          loading={this.state.assignmentsLoading}
          disableCloseOnSelect
          isOptionEqualToValue={(option, value) =>
            option.applicationId === value.applicationId
          }
          renderOption={(props, option, { selected }) => {
            const { key, ...optionProps } = props;
            const optionKey = option.applicationId ?? key;
            return (
              <li key={optionKey} {...optionProps}>
                <Checkbox checked={selected} />
                {`${option.title} (${option.instrumentTitle})`}
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Aplicações"
              helperText="Comece a digitar para buscar as aplicações desejadas."
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {this.state.assignmentsLoading && (
                      <CircularProgress color="inherit" size={20} />
                    )}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          ListboxProps={{
            style: {
              maxHeight: 200,
            },
          }}
          noOptionsText="Nenhuma aplicação encontrada"
          sx={{
            width: "100%",
          }}
        />
        <Box sx={{ display: "flex", justifyContent: "center", pt: 1 }}>
          <ReCAPTCHA
            ref={this.recaptchaRef}
            sitekey={environment.google.recaptchaToken}
            onChange={() => this.onRecaptchaChange()}
          />
        </Box>

      </Box>
    );
  }

  render() {
    return (
      <Dialog
        open
        onClose={() => this.didClickClose()}
        variant="modal"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle variant="modal">
          <PersonAddIcon/>
          Convidar usuário
        </DialogTitle>
        <DialogContent variant="modal">
          {this.state.isLoading || this.state.isSubmitting
            ? this.renderLoading()
            : this.renderForm()}
        </DialogContent>
        {!this.state.isLoading && !this.state.isSubmitting && (
          <DialogActions
            variant="modal"
            sx={{ flexWrap: "wrap", gap: 1, justifyContent: "end" }}
          >
            <Button
              variant="outlined"
              color="error"
              onClick={() => this.didClickClose()}
            >
              Cancelar
            </Button>

            <Button
              variant="contained"
              onClick={() => this.didClickInviteUsers()}
              disabled={
                this.state.email.length === 0 ||
                this.state.firstName.length === 0 ||
                this.state.lastName.length === 0 ||
                this.state.recaptchaToken.length === 0
              }
            >
              Convidar Usuário
            </Button>
          </DialogActions>
        )}
      </Dialog>
    );
  }
}

export default InviteUserModal;
